import React, { useState, useEffect, useRef, useCallback } from 'react-v18';
import ProgressBar from './ProgressBar';

const Player = ({ id, file, thumbnail, title, pod }) => {
    const [time, setTime] = useState('00:00');
    const [status, setStatus] = useState('init');
    const [timeProgress, setTimeProgress] = useState(0);
    const [duration, setDuration] = useState(0);

    const playerRef = useRef(null);
    const progressBarRef = useRef();

    useEffect(() => {
        let localStatus = JSON.parse(localStorage.getItem('audio_'+id));
        console.log('localStatus', localStatus);

        // Use callbacks to make sure current data is being used
        setTime((localStatus) => { return (localStatus && localStatus.time) ? localStatus.time : '00:00' });
        setStatus((localStatus) => { return (localStatus && localStatus.status) ? localStatus.status : 'init' });

        if (localStatus && localStatus.time) {
            const [minutes, seconds] = localStatus.time.split(':').map(Number);
            const totalMilliseconds = minutes * 60 + seconds;
            playerRef.current.currentTime = totalMilliseconds;
        }
    }, [])

    useEffect(() => {
        localStorage.setItem('audio_'+id, JSON.stringify({
            time: time,
            status: status
        }));
    }, [time, status])

    useEffect(() => {
        let progress = formatTime(timeProgress);
        if (progress !== time) {
            setTime(progress);
        }
    }, [timeProgress])

    const playAnimationRef = useRef();

    const formatTime = (stamp) => {
        if (stamp && !isNaN(stamp)) {
            const minutes = Math.floor(stamp / 60);
            const formatMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
            const seconds = Math.floor(stamp % 60);
            const formatSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;

            return `${formatMinutes}:${formatSeconds}`;
        }

        return '00:00';
    };

    const repeat = useCallback(() => {
        const currentTime = playerRef.current.currentTime;
        setTimeProgress(currentTime);
        progressBarRef.current.value = currentTime;
        progressBarRef.current.style.setProperty(
            '--range-progress',
            `${(progressBarRef.current.value / duration) * 100}%`
        );

        playAnimationRef.current = requestAnimationFrame(repeat);
    }, [playerRef, duration, progressBarRef, setTimeProgress]);

    const beforeUnload = useCallback((event) => { 
        event.preventDefault();
        event.returnValue = '';

        const confirmationMessage = 'Om du stänger fönstret kommer podden att sluta spelas.';
        (event || window.event).returnValue = confirmationMessage;
        return confirmationMessage;
    }, [])

    useEffect(() => {
        window.removeEventListener('beforeunload', beforeUnload, true);

        if (status === 'playing') {
            playerRef.current.play();

            window.addEventListener('beforeunload', beforeUnload, true);
        } else {
            playerRef.current.pause();
        }

        playAnimationRef.current = requestAnimationFrame(repeat);
    }, [status, playerRef, repeat]);

    const onLoadedMetadata = () => {
        const seconds = playerRef.current.duration;
        setDuration(seconds);
        progressBarRef.current.max = seconds;
    };

    const ended = () => {
        setStatus('stopped');
    }

    return (
        <div className="flex pb-4">
            <audio 
                ref={ playerRef }
                src={ file }
                onLoadedMetadata={ onLoadedMetadata }
                onEnded={ ended }
            />
            <div className="w-1/4 flex-shrink-0">
                <img src={ thumbnail ?? thumbnailPlaceholder } alt={ pod+': '+title } />
            </div>

            <div 
                className="relative w-3/4 bg-cover bg-center"
                style={{ backgroundImage: 'url('+thumbnail ?? thumbnailPlaceholder+')' }}
            >
                <div className="absolute w-full h-full top-0 left-0 p-2 flex md:p-4 lg:p-6 items-end text-white bg-pod-base/75 backdrop-blur">
                    <div className="w-full flex items-center gap-2 md:gap-4">
                        <div 
                            onClick={ () => setStatus(status === 'playing' ? 'paused' : 'playing') }
                            className="bg-pod-dark flex-none w-12 p-2 cursor-pointer transition-colors hover:bg-pod-base"
                        >
                            { status === 'playing' &&
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-full">
                                    <path fillRule="evenodd" d="M6.75 5.25a.75.75 0 01.75-.75H9a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75H7.5a.75.75 0 01-.75-.75V5.25zm7.5 0A.75.75 0 0115 4.5h1.5a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75H15a.75.75 0 01-.75-.75V5.25z" clipRule="evenodd" />
                                </svg>
                            || status === 'stopped' &&
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-full">
                                    <path fillRule="evenodd" d="M4.5 7.5a3 3 0 013-3h9a3 3 0 013 3v9a3 3 0 01-3 3h-9a3 3 0 01-3-3v-9z" clipRule="evenodd" />
                                </svg>
                            || 
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" stroke="currentColor" className="w-full">
                                    <path fill="white" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
                                </svg>
                            }
                        </div>

                        <div className="w-full overflow-hidden">
                            <div className="w-full mb-1 font-medium leading-none truncate">{ title }</div>

                            <ProgressBar 
                                {...{ 
                                    progressBarRef, 
                                    playerRef, 
                                    timeProgress, 
                                    duration, 
                                    time, 
                                    setTime,
                                    formatTime
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
export default Player;